import AutoGraphIcon from '@mui/icons-material/AutoGraph';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import GroupsIcon from '@mui/icons-material/Groups';
import SettingsIcon from '@mui/icons-material/Settings';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import TerminalIcon from '@mui/icons-material/Terminal';
import { List, ListItem, ListItemButton, ListItemIcon, ListItemText } from '@mui/material';
import React, { useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { campaignRoutes, settingsRoutes } from '../../../routes';
import { useAppSelector } from '../../../store/hooks';
import { useUserIsAdmin, useUserIsSupport, useUserIsTechSupport } from '../../../store/users';
import { DrawerComponents } from '../PageLayout';

type Props = {
    drawerComponent: DrawerComponents | null;
};

export function NavList({ drawerComponent }: Props) {
    const isSupport = useUserIsSupport();
    const isTechSupport = useUserIsTechSupport();
    const isAdmin = useUserIsAdmin();
    const clientHasWebPush = useAppSelector(state => state.user.clientHasWebPush);
    const { pathname } = useLocation();
    const history = useHistory();

    const groups = useMemo(
        () => buildNavGroups(drawerComponent, isAdmin, isSupport, isTechSupport, clientHasWebPush),
        [drawerComponent, isAdmin, isSupport, isTechSupport, clientHasWebPush],
    );

    const handleClick = (pathname: string) => {
        history.push(pathname);
    };

    return (
        <div className="settingsNav">
            <div className="settingsNav__list">
                {groups &&
                    groups.map(group => (
                        <div key={group.name}>
                            <List sx={{ p: 0 }}>
                                <ListItem sx={{ mb: 1, mt: 1 }}>
                                    <ListItemIcon>{group.icon}</ListItemIcon>
                                    <ListItemText
                                        primary={group.name}
                                        primaryTypographyProps={{ sx: { fontWeight: 600 } }}
                                    />
                                </ListItem>
                                {group.items.map(item => (
                                    <ListItem key={item.name} sx={{ p: '2px 8px 2px 8px' }}>
                                        <ListItemButton
                                            sx={{ borderRadius: 1 }}
                                            selected={pathname === item.pathname}
                                            onClick={() => {
                                                handleClick(item.pathname);
                                            }}
                                        >
                                            <ListItemIcon>
                                                <ChevronRightIcon sx={{ fontSize: '18px' }} />
                                            </ListItemIcon>
                                            <ListItemText primary={item.name} />
                                        </ListItemButton>
                                    </ListItem>
                                ))}
                            </List>
                        </div>
                    ))}
            </div>
        </div>
    );
}

const buildNavGroups = (
    drawerComponent: DrawerComponents | null,
    isAdmin: boolean,
    isSupport: boolean,
    isTechSupport: boolean,
    clientHasWebPush: boolean,
): NavGroup[] | null => {
    if (!drawerComponent) return null;

    let group = [...staticSidebarOptions[drawerComponent]];

    //add whatever nav items are needed based on user flags here
    if (drawerComponent === 'settings') {
        if (isAdmin) {
            group.push(adminSettings);
        }

        if (isSupport) {
            group.push(webSupportSettings);
        }

        if (isTechSupport) {
            group.push(techSupportSettings);
        }
    }

    if (drawerComponent === 'campaigns') {
        if (clientHasWebPush) {
            group.push({
                name: 'Campaigns',
                icon: <AutoGraphIcon />,
                items: [
                    {
                        name: 'Web Push',
                        pathname: campaignRoutes.webPush,
                    },
                ],
            });
        } else {
            group.push({
                name: 'Campaigns',
                icon: <AutoGraphIcon />,
                items: [],
            });
        }
    }

    return group;
};

const staticSidebarOptions: Navigation = {
    settings: [
        {
            name: 'User Settings',
            icon: <GroupsIcon />,
            items: [
                {
                    name: 'Ai',
                    pathname: settingsRoutes.ai,
                },
                {
                    name: 'Change Password',
                    pathname: settingsRoutes.changePassword,
                },
                // {
                //     name: 'Calendar',
                //     pathname: settingsRoutes.calendar,
                // },
                {
                    name: 'Notifications',
                    pathname: settingsRoutes.notification,
                },
                {
                    name: 'User Email',
                    pathname: settingsRoutes.userEmail,
                },
                {
                    name: 'Phone',
                    pathname: settingsRoutes.phoneSettings,
                },
                {
                    name: 'Google',
                    pathname: settingsRoutes.google,
                },
            ],
        },
    ],
    campaigns: [],
    'new/Recent': [],
    groups: [],
    help: [],
};

const adminSettings: NavGroup = {
    name: 'Administration',
    icon: <SettingsIcon />,
    items: [
        {
            name: 'General',
            pathname: settingsRoutes.general,
        },
        {
            name: 'Custom Fields',
            pathname: settingsRoutes.customFields,
        },
        {
            name: 'Custom Views',
            pathname: settingsRoutes.customViews,
        },
        {
            name: 'User Management',
            pathname: settingsRoutes.users,
        },
        // {
        //     name: 'Package Usage',
        //     pathname: settingsRoutes.packageUsage,
        // },
        {
            name: 'Integrations',
            pathname: settingsRoutes.integrations,
        },
        // {
        //     name: 'Import/Export',
        //     pathname: routes.importExport,
        // },
        // {
        //     name: 'Contact Status',
        //     pathname: routes.contactStatus,
        // },
    ],
};

const webSupportSettings: NavGroup = {
    name: 'Web Support',
    icon: <SupportAgentIcon />,
    items: [],
};

const techSupportSettings: NavGroup = {
    name: 'Tech Support',
    icon: <TerminalIcon />,
    items: [
        {
            name: 'Ai Prompts',
            pathname: settingsRoutes.aiPrompts,
        },
        {
            name: 'Ai SmartReply Plans',
            pathname: settingsRoutes.aiSmartReply,
        },
    ],
};

type NavItem = {
    name: string;
    pathname: string;
};

type NavGroup = {
    name: string;
    icon: React.ReactElement;
    items: NavItem[];
};

type Navigation = Record<DrawerComponents, NavGroup[]>;
